import { useMemo } from 'react';
import { Typography, Tooltip } from 'antd';
import { isGuestPerson } from '../../utils/guest_session';

const { Text } = Typography;

/**
 * How the groups overlap, as an UpSet-style list: one row per distinct
 * combination of groups a person can be in, with a dot per group and a bar
 * for how many people share exactly that combination. Reads better than a
 * Venn past three groups, and makes the "in several groups" people — the
 * ones the planner can move between seat types — easy to spot.
 */
function GroupOverlap({ groups, colorOf, membersOf, pool, peopleById }) {
    const combos = useMemo(() => {
        const bySig = new Map();
        for (const id of pool) {
            const inGroups = groups.filter(g => membersOf.get(g.id)?.has(id)).map(g => g.id);
            const sig = inGroups.join('|');
            if (!bySig.has(sig)) bySig.set(sig, { groupIds: inGroups, ids: [] });
            bySig.get(sig).ids.push(id);
        }
        return [...bySig.values()].sort((a, b) => b.ids.length - a.ids.length || b.groupIds.length - a.groupIds.length);
    }, [groups, membersOf, pool]);

    if (groups.length === 0 || pool.length === 0) return null;
    const max = Math.max(...combos.map(c => c.ids.length));
    const multi = combos.filter(c => c.groupIds.length > 1).reduce((n, c) => n + c.ids.length, 0);

    return (
        <div className="gathering-overlap">
            <div className="gathering-overlap-head">
                <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                    Overlap — {multi} of {pool.length} {pool.length === 1 ? 'person is' : 'people are'} in more than one group
                </Text>
                <div className="gathering-overlap-legend">
                    {groups.map(g => (
                        <span key={g.id} className="gathering-overlap-legend-item">
                            <span className="gathering-dot" style={{ background: colorOf(g.id) }} />
                            {g.name || 'Untitled'}
                        </span>
                    ))}
                </div>
            </div>
            {combos.map(c => {
                const names = c.ids.map(id => peopleById.get(id))
                    .filter(p => p?.name)
                    .map(p => (isGuestPerson(p) ? `${p.name} (guest)` : p.name));
                return (
                    <div key={c.groupIds.join('|') || 'none'} className="gathering-overlap-row">
                        <div className="gathering-overlap-dots">
                            {groups.map(g => (
                                <span
                                    key={g.id}
                                    className="gathering-dot"
                                    style={c.groupIds.includes(g.id)
                                        ? { background: colorOf(g.id) }
                                        : { background: 'var(--border-color-soft)' }}
                                />
                            ))}
                        </div>
                        <Tooltip title={names.slice(0, 12).join(', ') + (names.length > 12 ? ` +${names.length - 12} more` : '')}>
                            <div className="gathering-overlap-bar-track">
                                <div
                                    className="gathering-overlap-bar"
                                    style={{
                                        width: `${(c.ids.length / max) * 100}%`,
                                        background: c.groupIds.length === 1 ? colorOf(c.groupIds[0]) : 'var(--text-tertiary)',
                                    }}
                                />
                            </div>
                        </Tooltip>
                        <Text className="gathering-overlap-count">{c.ids.length}</Text>
                        <Text type="secondary" className="gathering-overlap-label" ellipsis>
                            {c.groupIds.length === 0 ? 'in no group (open seats only)' : c.groupIds.length > 1 ? 'overlap' : 'only'}
                        </Text>
                    </div>
                );
            })}
        </div>
    );
}

export default GroupOverlap;
